/**
 * The public support page (SUP-001, SUP-003, SUP-005). Answers the questions a customer actually
 * asks, in their words, without inventing a legal policy we have not written. The reaction path
 * is a separate band, not another FAQ row.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { dark } from "@lattice/tokens/themes";
import { focusRing } from "@lattice/ui/focus";
import { Link } from "@lattice/ui/Link";
import { LinkButton } from "@lattice/ui/LinkButton";
import { loadMe } from "./api.ts";
import { PublicPage, SUPPORT_ADDRESS, supportMailto } from "./PublicPage.tsx";
const WIDE = "@media (min-width: 960px)";
const MID = "@media (min-width: 720px)";
const styles = {
  mail: {
    kMwMTN: "xzb3lse",
    kybGjl: "x1bvjpef x1lku1pv",
    kcSHmL: "xda060z",
    kaIpWk: "x8wwnw1",
    $$css: true
  },
  reaction: {
    kaIpWk: "x1hegsvj",
    kWkggS: "x1l0w1cl",
    kMwMTN: "xzb3lse",
    kmVPX3: "x14vxv0r",
    k1xSpc: "xrvj5dj",
    kumcoG: "x1y6fwsi x1g5rves",
    k1C7PZ: "xtd2lgx",
    khm7nJ: "xbnhjcb",
    kGNEyG: "x6s0dn4",
    $$css: true
  }
};
interface Question {
  readonly id: string;
  readonly q: string;
  readonly a: readonly ReactNode[];
}
interface Group {
  readonly id: string;
  readonly title: string;
  readonly questions: readonly Question[];
}
const GROUPS: readonly Group[] = [{
  id: "formula",
  title: "Your formula",
  questions: [{
    id: "review",
    q: "How does a physician review work?",
    a: ["A physician reviews your labs and answers within 24 hours.", "They sign a formula or write to you if they cannot."]
  }, {
    id: "ingredients",
    q: "What is each ingredient for?",
    a: ["Your formula lists every ingredient, its dose, and why it is there.", <>
            <Link href="/account/formula">Open your formula</Link> to see yours.
          </>]
  }, {
    id: "leave-out",
    q: "Can I leave an ingredient out?",
    a: [<>
            Yes. <Link href="/account/formula">Open your formula</Link> in your account and remove
            it.
          </>, "It leaves your next shipment. The protein base stays in every formula."]
  }]
}, {
  id: "orders",
  title: "Shipping and billing",
  questions: [{
    id: "shipping",
    q: "When does a shipment go out?",
    a: ["A pharmacy ships each month after a physician signs your formula.", "You will see tracking when they send it."]
  }, {
    id: "cancel",
    q: "Can I pause or cancel?",
    a: [<>
            Pause, skip a month, move a ship date, or cancel from{" "}
            <Link href="/account">your account</Link>.
          </>, "Cancelling stops your next shipment. Your physician stops reviewing your labs."]
  }, {
    id: "billing",
    q: "How does billing work?",
    a: ["$175 a month, charged only after your physician approves your formula."]
  }]
}, {
  id: "privacy",
  title: "Your labs and privacy",
  questions: [{
    id: "labs",
    q: "What do you do with my labs?",
    a: ["We use them to draft your formula. We do not sell them.", <>
            Download a copy or ask for deletion from the{" "}
            <Link href="/account/privacy">privacy page</Link> in your account.
          </>]
  }]
}];
export function Support() {
  const [accountId, setAccountId] = useState<string | null>(null);
  useEffect(() => {
    /* A failed lookup is treated as signed out, so the page still gives the way to write to us. */
    void loadMe().catch(() => null).then(me => setAccountId(me?.userId ?? null));
  }, []);
  return <PublicPage title="Support · Lattice">
      <div className="x78zum5 xdt5ytf xd3upel">
        <div className="x78zum5 xdt5ytf x15agyyk xc0i79g">
          <h1 className="x1ghz6dp x1u29x3t xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw">Help with your formula</h1>
          <p className="x1ghz6dp x1jty25b x1fzhlzt x1hw683e">
            Answers about review, ingredients, shipping, billing and privacy. Write to{" "}
            <a href={supportMailto(accountId)} {...stylex.props(styles.mail, focusRing)}>
              {SUPPORT_ADDRESS}
            </a>{" "}
            for anything else.
          </p>
        </div>
        <section {...stylex.props(dark, styles.reaction)} aria-labelledby="reaction-heading" data-testid="reaction-path">
          <div className="x78zum5 xdt5ytf x626il7 xeuugli">
            <h2 id="reaction-heading" className="x1ghz6dp x3cmmws xqu410z x1d3j7mx x1e5flnj x1w2vvpw">
              Think the formula made you unwell?
            </h2>
            <p className="x1ghz6dp xzb3lse x1fzhlzt x19pysoa">
              Stop taking it. Call 911 if this is an emergency. Then tell a physician what happened.
            </p>
          </div>
          <LinkButton href="/support/reaction">Report a reaction</LinkButton>
        </section>
        <nav aria-label="Questions by topic">
          <ul className="x78zum5 x1a02dak x1iojj6j x1xwure6 xe8uvvx x1ghz6dp x1717udv">
            {GROUPS.map(group => <li key={group.id} className="x78zum5 x6s0dn4 x1gg8mnh">
                <Link href={`#${group.id}`}>{group.title}</Link>
              </li>)}
          </ul>
        </nav>
        {GROUPS.map(group => <section key={group.id} id={group.id} aria-labelledby={`${group.id}-title`} className="x78zum5 xdt5ytf x626il7 x1dx31gp">
            <h2 id={`${group.id}-title`} className="x1ghz6dp x1u29x3t xqu410z x1d3j7mx x1e5flnj">
              {group.title}
            </h2>
            <ol className="xe8uvvx x1ghz6dp x1717udv">
              {group.questions.map(item => <li key={item.id} id={item.id} className="xrvj5dj x1y6fwsi x1r6w3ig xtmfs0b xqecrbx xfjredr x1dx31gp xez8t42 x13fuv20 x178xt8z">
                  <h3 className="x1ghz6dp x3cmmws xqu410z x1d3j7mx x1e5flnj x1w2vvpw">{item.q}</h3>
                  <div className="x1ghz6dp x1jty25b x1fzhlzt x78zum5 xdt5ytf x1173whf">
                    {item.a.map((line, index) => <p key={`${item.id}-${index}`} className="x1ghz6dp x1jty25b x1fzhlzt x1hw683e">
                        {line}
                      </p>)}
                  </div>
                </li>)}
            </ol>
          </section>)}
      </div>
    </PublicPage>;
}